import { Selector } from './Selectors'

/**
 * Minimal DOM helpers.
 * - Zero caching: every call queries the DOM fresh.
 * - Accepts a CSS selector string, an Element, a NodeList, or an array of those.
 * - You can scope queries by passing a custom `root` (defaults to `document`).
 */

const DEFAULT_ROOT =
    typeof window !== 'undefined' && window.document ? window.document : typeof document !== 'undefined' ? document : undefined

export const DOM = {
    /**
     * Get the first matching element.
     * @param {string|Element} sel - CSS selector or Element.
     * @param {Document|HTMLElement} [root=document] - Query root.
     * @returns {Element|null}
     */
    get(sel, root = DEFAULT_ROOT) {
        if (!root) return null
        if (sel == null || sel === '') return null
        if (typeof Element !== 'undefined' && sel instanceof Element) return sel
        if (typeof sel !== 'string') return null
        return root.querySelector(sel)
    },

    /**
     * Get the first matching element without logging when missing.
     * @param {string|Element} sel - CSS selector or Element.
     * @param {Document|HTMLElement} [root=document] - Query root.
     * @returns {Element|null}
     */
    getOptional(sel, root = DEFAULT_ROOT) {
        if (!root) return null
        if (sel == null || sel === '') return null
        if (sel instanceof Element) return sel
        if (typeof sel !== 'string') return null
        return root.querySelector(sel) || null
    },

    /**
     * Get all matching elements as a flat array.
     * @param {string|Element|NodeList|Array<string|Element|NodeList>} sel - One or many selectors/elements.
     * @param {Document|HTMLElement} [root=document] - Query root.
     * @returns {Element[]}
     */
    all(sel, root = DEFAULT_ROOT) {
        if (!root) return []
        if (sel == null || sel === '') return []
        if (typeof sel === 'string') {
            return Array.from(root.querySelectorAll(sel))
        }
        if (typeof Element !== 'undefined' && sel instanceof Element) return [sel]
        if (typeof NodeList !== 'undefined' && sel instanceof NodeList) return Array.from(sel)
        if (Array.isArray(sel)) return sel.flatMap((s) => this.all(s, root))
        return []
    },

    /**
     * Show element(s) (jQuery-like).
     * Removes the [hidden] attribute and clears inline display:none.
     * @param {string|Element|NodeList|Array<string|Element|NodeList>} target
     * @param {Document|HTMLElement} [root=document]
     */
    show(target, root = DEFAULT_ROOT) {
        const list = this.all(target, root)
        for (const el of list) {
            // Remove attribute-based and inline hiding first
            el.hidden = false // removes the [hidden] attribute effect
            el.style.removeProperty('visibility')
            el.style.removeProperty('display') // clear old inline override, let CSS apply

            // If the stylesheet still hides it (display: none),
            // we MUST override it, otherwise it stays invisible.
            if (getComputedStyle(el).display === 'none') {
                el.style.display = 'block' // fallback: explicitly show it
            }
        }
    },

    /**
     * Show one optional element if it exists.
     * Returns false when nothing matched.
     * @param {string|Element} target
     * @param {Document|HTMLElement} [root=document]
     * @returns {boolean}
     */
    showIfExists(target, root = DEFAULT_ROOT) {
        const el = this.getOptional(target, root)
        if (!el) return false
        this.show(el, root)
        return true
    },

    /**
     * Hide element(s) (jQuery-like).
     * Sets [hidden] and inline display:none.
     * @param {string|Element|NodeList|Array<string|Element|NodeList>} target
     * @param {Document|HTMLElement} [root=document]
     */
    hide(target, root = DEFAULT_ROOT) {
        const list = this.all(target, root)
        for (const el of list) {
            el.hidden = true
            el.style.display = 'none'
            el.style.visibility = 'hidden'
        }
    },

    /**
     * Hide one optional element if it exists.
     * Returns false when nothing matched.
     * @param {string|Element} target
     * @param {Document|HTMLElement} [root=document]
     * @returns {boolean}
     */
    hideIfExists(target, root = DEFAULT_ROOT) {
        const el = this.getOptional(target, root)
        if (!el) return false
        this.hide(el, root)
        return true
    },

    /**
     * Toggle element(s) visibility.
     * If `force` is boolean, show/hide accordingly; otherwise invert current state.
     * @param {string|Element|NodeList|Array<string|Element|NodeList>} target
     * @param {boolean} [force]
     * @param {Document|HTMLElement} [root=document]
     */
    toggle(target, force, root = DEFAULT_ROOT) {
        const list = this.all(target, root)
        for (const el of list) {
            const cs = getComputedStyle(el)
            const visible = !el.hidden && cs.display !== 'none' && cs.visibility !== 'hidden'
            const shouldShow = typeof force === 'boolean' ? force : !visible
            shouldShow ? this.show(el, root) : this.hide(el, root)
        }
    },

    /**
     * Scroll to an element by its id.
     * Accepts 'preview' or '#preview'.
     * @param {string} idInput
     * @param {Object} [opts]
     * @param {Document|HTMLElement} [opts.root=DEFAULT_ROOT]
     * @param {HTMLElement|null} [opts.container=null]
     * @param {number} [opts.offset=0]
     * @param {'auto'|'smooth'} [opts.behavior='smooth']
     * @param {boolean} [opts.focus=false]
     * @param {boolean} [opts.updateHash=false]
     * @returns {Element|null}
     */
    scrollToId(idInput, opts = {}) {
        const { root = DEFAULT_ROOT, container = null, offset = 0, behavior = 'smooth', focus = false, updateHash = false } = opts

        if (!root) return null
        if (!idInput || typeof idInput !== 'string') return null

        // Normalize: strip any leading '#' so '#preview' works.
        const id = idInput.replace(/^#+/, '')

        // Escape for querySelector fallback
        const esc = (s) =>
            typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/([ #;?%&,.+*~':"!^$[\]()=>|/@])/g, '\\$1')

        // Find element by id, then fallback to querySelector
        const el = root.getElementById(id) || root.querySelector('#' + esc(id))
        if (!el) return null

        const hasWindow = typeof window !== 'undefined' && !!window.document
        if (!hasWindow) return el

        const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const usedBehavior = reduced ? 'auto' : behavior

        if (container instanceof HTMLElement) {
            const elRect = el.getBoundingClientRect()
            const cRect = container.getBoundingClientRect()
            const targetTop = container.scrollTop + (elRect.top - cRect.top) - offset
            container.scrollTo({ top: Math.max(0, targetTop), behavior: usedBehavior })
        } else {
            const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0
            const targetY = el.getBoundingClientRect().top + scrollY - offset
            window.scrollTo({ top: Math.max(0, targetY), behavior: usedBehavior })
        }

        if (focus && typeof el.focus === 'function') {
            try {
                el.focus({ preventScroll: true })
            } catch {
                el.focus()
            }
        }

        if (updateHash && typeof history !== 'undefined' && history.pushState) {
            const base = window.location.href.split('#')[0]
            history.pushState(null, '', `${base}#${esc(id)}`)
        }

        return el
    },

    /** Normalize any string to Unicode NFC (safe for null/undefined). */
    normalizeNFC(s) {
        return (s ?? '').toString().normalize('NFC')
    },

    /** Read an input’s `.value` and return it normalized to NFC. */
    valueNFC(sel, root = DEFAULT_ROOT) {
        const el = this.get(sel, root)
        const v = el && 'value' in el ? el.value : ''
        return this.normalizeNFC(v)
    },

    loading(isLoading = true) {
        const overlay = DOM.get(Selector.loadingOverlay)
        if (!overlay) return
        overlay.style.display = isLoading ? 'block' : 'none'
    },
}
window.DOM = DOM
